import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button } from '@img/ui';
import { api, type ApiSuccess } from '../../lib/api';

interface ActivityItem {
  type: string;
  icon: string;
  color: string;
  title: string;
  detail: string;
  refId: string;
  at: string;
  ip: string | null;
  geo: { city: string | null; country: string | null; region: string | null };
  userAgent: string | null;
}

interface ActivityResponse {
  hours: number;
  items: ActivityItem[];
  stats: { total: number; byType: Record<string, number> };
}

const TYPES: Array<{ id: string; label: string; icon: string; color: 'navy' | 'green' | 'amber' | 'red' | 'gold' | 'gray' }> = [
  { id: '',         label: 'الكل',         icon: '📊', color: 'navy' },
  { id: 'admin',    label: 'إجراءات الإدارة', icon: '👑', color: 'gold' },
  { id: 'login',    label: 'تسجيل دخول',   icon: '🔐', color: 'green' },
  { id: 'user',     label: 'مستخدمون',     icon: '👤', color: 'navy' },
  { id: 'property', label: 'عقارات',       icon: '🏠', color: 'green' },
  { id: 'auction',  label: 'مزادات',       icon: '🔨', color: 'red' },
  { id: 'bid',      label: 'مزايدات',      icon: '💰', color: 'gold' },
  { id: 'request',  label: 'طلبات',        icon: '📋', color: 'amber' },
  { id: 'message',  label: 'رسائل',        icon: '✉️', color: 'navy' },
];

interface UserLite {
  id: string;
  name: string;
  email: string | null;
  phone: string | null;
  role: string;
}

export function AdminActivityLogPage() {
  const [type, setType] = useState('');
  const [hours, setHours] = useState(24);
  const [selectedUser, setSelectedUser] = useState<UserLite | null>(null);
  const [searchInput, setSearchInput] = useState('');
  const [showSearch, setShowSearch] = useState(false);

  const { data, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['admin', 'activity', { type, hours, userId: selectedUser?.id }],
    queryFn: () => {
      const params = new URLSearchParams({ hours: String(hours), limit: '200' });
      if (type) params.set('type', type);
      if (selectedUser?.id) params.set('userId', selectedUser.id);
      return api.get<ApiSuccess<ActivityResponse>>(`/api/admin/activity?${params.toString()}`);
    },
    refetchInterval: 60_000,
  });

  // 🔎 بحث عن مستخدم
  const { data: usersData } = useQuery({
    queryKey: ['admin', 'users-search', searchInput],
    queryFn: () => api.get<ApiSuccess<UserLite[]>>(`/api/admin/users-search?q=${encodeURIComponent(searchInput)}`),
    enabled: showSearch,
  });

  const items = data?.data.items ?? [];
  const stats = data?.data.stats;

  return (
    <div>
      <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
        <div>
          <h1 className="text-3xl font-black text-navy-dark mb-1">📊 سجل النشاط</h1>
          <p className="text-gray-600 text-sm">كل العمليات على المنصة مع IP والموقع الجغرافي</p>
        </div>
        <Button variant="ghost" onClick={() => refetch()} loading={isFetching}>🔄 تحديث</Button>
      </div>

      {/* 🔎 فلتر مستخدم محدد */}
      <Card className="mb-4">
        <CardBody>
          {selectedUser ? (
            <div className="flex items-center gap-3 flex-wrap">
              <span className="text-sm text-gray-500">يعرض نشاط:</span>
              <Badge color="navy" className="!text-sm">
                {selectedUser.name} • {selectedUser.email || selectedUser.phone || ''} ({selectedUser.role})
              </Badge>
              <button onClick={() => setSelectedUser(null)} className="text-xs text-red-600 hover:text-red-700 font-bold">
                ✕ إزالة الفلتر
              </button>
            </div>
          ) : (
            <>
              <button
                onClick={() => setShowSearch(!showSearch)}
                className="text-sm font-bold text-navy-dark hover:text-gold flex items-center gap-2"
              >
                🔎 فلترة حسب مستخدم محدد {showSearch ? '▲' : '▼'}
              </button>
              {showSearch && (
                <div className="mt-3 space-y-2">
                  <input
                    type="text"
                    placeholder="ابحث بالاسم/الإيميل/الجوال…"
                    value={searchInput}
                    onChange={(e) => setSearchInput(e.target.value)}
                    className="w-full px-4 py-2.5 rounded-xl border-2 border-gray-200 focus:border-navy outline-none bg-white text-sm"
                    autoFocus
                  />
                  {usersData?.data && usersData.data.length > 0 && (
                    <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-64 overflow-y-auto">
                      {usersData.data.map((u) => (
                        <button
                          key={u.id}
                          onClick={() => { setSelectedUser(u); setShowSearch(false); setSearchInput(''); }}
                          className="w-full text-right p-3 hover:bg-gray-50 flex items-center justify-between"
                        >
                          <div>
                            <div className="font-bold text-ink text-sm">{u.name}</div>
                            <div className="text-xs text-gray-500">{u.email || u.phone}</div>
                          </div>
                          <Badge color="gray">{u.role}</Badge>
                        </button>
                      ))}
                    </div>
                  )}
                  {usersData?.data && usersData.data.length === 0 && searchInput && (
                    <p className="text-xs text-gray-500 text-center py-2">لا يوجد نتائج</p>
                  )}
                </div>
              )}
            </>
          )}
        </CardBody>
      </Card>

      {/* فترة زمنية */}
      <div className="flex gap-2 mb-3 overflow-x-auto pb-1">
        {[1, 6, 24, 72, 168, 720].map((h) => (
          <button
            key={h}
            onClick={() => setHours(h)}
            className={`px-4 py-2 rounded-xl text-sm font-bold shrink-0 transition ${
              hours === h ? 'bg-navy-dark text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
            }`}
          >
            {h === 1   ? 'آخر ساعة'
            : h === 6   ? 'آخر 6 ساعات'
            : h === 24  ? 'آخر 24 ساعة'
            : h === 72  ? 'آخر 3 أيام'
            : h === 168 ? 'آخر أسبوع'
            : 'آخر 30 يوم'}
          </button>
        ))}
      </div>

      {/* فلتر النوع */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        {TYPES.map((t) => {
          const count = t.id === '' ? stats?.total : stats?.byType[t.id];
          return (
            <button
              key={t.id}
              onClick={() => setType(t.id)}
              className={`px-4 py-2 rounded-xl text-sm font-bold shrink-0 transition flex items-center gap-2 ${
                type === t.id ? 'bg-navy-dark text-white' : 'bg-white text-gray-600 hover:bg-gray-100'
              }`}
            >
              <span>{t.icon}</span>
              <span>{t.label}</span>
              {count !== undefined && (
                <span className={`text-xs px-2 py-0.5 rounded-full ${
                  type === t.id ? 'bg-white/20' : 'bg-gray-100'
                }`}>
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* القائمة */}
      <Card>
        <CardBody className="!p-0">
          {isLoading ? (
            <div className="text-center py-12 text-gray-500">⟳ جاري التحميل…</div>
          ) : items.length === 0 ? (
            <div className="text-center py-12 text-gray-500">لا يوجد نشاط في هذه الفترة</div>
          ) : (
            <div className="divide-y divide-gray-100">
              {items.map((item, i) => (
                <ActivityRow key={`${item.type}-${item.refId}-${i}`} item={item} />
              ))}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function ActivityRow({ item }: { item: ActivityItem }) {
  return (
    <div className="flex items-start gap-4 p-4 hover:bg-gray-50 transition">
      <div
        className="w-12 h-12 rounded-full flex items-center justify-center shrink-0 text-2xl"
        style={{ background: `${item.color}22`, color: item.color }}
      >
        {item.icon}
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-black text-ink">{item.title}</span>
          <Badge color="gray">{item.type}</Badge>
        </div>
        <div className="text-sm text-gray-600 mt-1">{item.detail}</div>

        {/* IP + Geo + UserAgent */}
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs">
          {item.ip && (
            <span className="text-gray-500">
              <span className="font-bold">IP:</span> <span dir="ltr">{item.ip}</span>
            </span>
          )}
          {(item.geo?.city || item.geo?.country) && (
            <span className="text-gray-500">
              📍 {[item.geo.city, item.geo.region, item.geo.country].filter(Boolean).join(' • ')}
            </span>
          )}
          {item.userAgent && (
            <span className="text-gray-500 truncate max-w-md" title={item.userAgent}>
              🖥️ {parseUA(item.userAgent)}
            </span>
          )}
        </div>
      </div>

      <div className="text-xs text-gray-400 shrink-0 text-left">
        <div>{new Date(item.at).toLocaleTimeString('ar-SA')}</div>
        <div>{new Date(item.at).toLocaleDateString('ar-SA')}</div>
      </div>
    </div>
  );
}

/** اقرأ user agent ببساطة لإظهار اسم المتصفح + النظام */
function parseUA(ua: string): string {
  const browser = /Edg\//.test(ua) ? 'Edge'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari'
    : 'Browser';
  const os = /Windows/.test(ua) ? 'Windows'
    : /Mac OS X/.test(ua) ? 'macOS'
    : /iPhone|iPad|iPod/.test(ua) ? 'iOS'
    : /Android/.test(ua) ? 'Android'
    : /Linux/.test(ua) ? 'Linux'
    : 'Unknown';
  return `${browser} • ${os}`;
}
